import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Form, Alert } from 'antd';
import { UpdatePackagesFeatureFlagsStore } from '../../stores';
import {
    FeatureFlagsOverride,
    setFormFeatureFlags,
    setFormFeatureFlagsDefaults,
    transformFeatureFlagsAsRecord
} from '../../../common/misc/FeatureFlagsHelpers';
import { FeatureFlagsRenderer } from '../../../common/components';
import { featureFlagProcessingLabels, PackageFeatureFlagsPreview } from '../../models/FeatureFlagProcessingPlan';
import { FeatureFlagsPreviewState } from '../useFeatureFlagsPreview';

import { FeatureFlagsPreview } from '../FeatureFlagsPreview';
import { HasPermission } from '../../../authorization/components/HasPermission';
import { AppPermissions } from '../../../authorization/Permissions';
import { featureFlagsPreviewKeyOf } from '../../models/FeatureFlagsPlanning';

const formId = 'update-packages-feature-flags-form';

interface Props {
    store: UpdatePackagesFeatureFlagsStore;
}

type PreviewState = FeatureFlagsPreviewState<PackageFeatureFlagsPreview[]>;

const UpdatePackagesFeatureFlagsModalContent: React.FC<Props> = observer(({ store }: Props) => {
    const [form] = Form.useForm();
    const [preview, setPreview] = React.useState<PreviewState>(null);
    const [saving, setSaving] = React.useState(false);
    const [refresh, setRefresh] = React.useState(0);
    const selectedIds = [...store.packagesToUpdate];

    const [featureFlagsOverride, setFeatureFlagsOverride] = React.useState<FeatureFlagsOverride>({
        overrideFeatureFlags: false,
        overrideImageFeatureFlags: false,
        overrideTextFeatureFlags: false
    });

    const handleClose = () => {
        store.setModalVisible(false);
    };

    const handleSubmit = async () => {
        const flags = transformFeatureFlagsAsRecord(form, featureFlagsOverride);
        const packageIds = [...store.packagesToUpdate];
        if (
            saving ||
            !preview?.value ||
            preview.key !== featureFlagsPreviewKeyOf(store.featureFlagsPlanning, [packageIds, flags])
        ) {
            return;
        }
        if (!preview.value.some(choice => choice.plan)) {
            return;
        }
        setSaving(true);
        try {
            const allStarted = await store.updatePackagesFeatureFlags(flags, preview.value, packageIds);
            if (!allStarted) {
                setRefresh(value => value + 1);
            }
        } finally {
            setSaving(false);
        }
    };

    React.useEffect(() => {
        if (store.modalVisible && store.projectFeatureFlags) {
            setFeatureFlagsOverride({
                ...setFormFeatureFlags(form, store.projectFeatureFlags)
            });
        } else {
            setFeatureFlagsOverride({
                ...setFormFeatureFlagsDefaults(form)
            });
        }
    }, [form, store.modalVisible, store.projectFeatureFlags]);

    const reprocess = preview?.value?.filter(choice => choice.plan?.stage === 'Interpretation').length || 0;
    const reparse = preview?.value?.filter(choice => choice.plan?.stage === 'Preparation').length || 0;
    const submitLabel =
        reprocess && reparse
            ? `Save: ${featureFlagProcessingLabels.Interpretation.action.toLowerCase()} ${reprocess}, ${featureFlagProcessingLabels.Preparation.action.toLowerCase()} ${reparse}`
            : reprocess
              ? `${featureFlagProcessingLabels.Interpretation.save} ${reprocess}`
              : reparse
                ? `${featureFlagProcessingLabels.Preparation.save} ${reparse}`
                : preview?.value
                  ? 'No eligible documents'
                  : 'Checking processing choices';

    return (
        <Modal
            className="alpha-portal-dialog"
            title="Override Feature Flags"
            visible={store.modalVisible}
            onCancel={handleClose}
            maskClosable={false}
            closable={false}
            width={600}
            destroyOnClose
            centered
            footer={[
                <Button key="close" size="large" className="light" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    size="large"
                    type="primary"
                    htmlType="submit"
                    form={formId}
                    disabled={saving || !(reprocess + reparse)}
                    loading={saving}
                >
                    {submitLabel}
                </Button>
            ]}
        >
            {preview?.error && (
                <Alert
                    type="error"
                    message={preview.error}
                    action={<Button onClick={() => setRefresh(value => value + 1)}>Refresh choices</Button>}
                />
            )}
            {preview?.value && (
                <div>
                    <p>
                        Will {featureFlagProcessingLabels.Interpretation.action.toLowerCase()}: {reprocess}. Will{' '}
                        {featureFlagProcessingLabels.Preparation.action.toLowerCase()}: {reparse}. Skipped:{' '}
                        {preview.value.length - reprocess - reparse}.
                    </p>
                    <ul>
                        {preview.value.map(choice => (
                            <li key={choice.packageId}>
                                <strong>{choice.fileName || choice.packageId}</strong>:{' '}
                                {choice.plan ? featureFlagProcessingLabels[choice.plan.stage].action : 'Skipped'}
                                {(choice.plan?.reason || choice.reason) && (
                                    <p>{choice.plan?.reason || choice.reason}</p>
                                )}
                            </li>
                        ))}
                    </ul>
                    <Button onClick={() => setRefresh(value => value + 1)} disabled={saving}>
                        Refresh choices
                    </Button>
                </div>
            )}
            <Form id={formId} form={form} onFinish={handleSubmit} onValuesChange={() => setPreview(null)}>
                {store.modalVisible && (
                    <Form.Item noStyle shouldUpdate>
                        {() => (
                            <FeatureFlagsPreview
                                args={[selectedIds, transformFeatureFlagsAsRecord(form, featureFlagsOverride, false)]}
                                planning={store.featureFlagsPlanning}
                                refresh={refresh}
                                onPreview={setPreview}
                            />
                        )}
                    </Form.Item>
                )}
                <FeatureFlagsRenderer
                    featureFlagsOverride={featureFlagsOverride}
                    setFeatureFlagsOverride={setFeatureFlagsOverride}
                    projectId={store.project?.id}
                />
            </Form>
        </Modal>
    );
});

const UpdatePackagesFeatureFlagsModal: React.FC<Props> = ({ store }) => (
    <HasPermission entityId={store.project?.id} permissionClaim={AppPermissions.CanEditProcessDocuments}>
        <UpdatePackagesFeatureFlagsModalContent store={store} />
    </HasPermission>
);

export default observer(UpdatePackagesFeatureFlagsModal);
